import React from "react";
import ReactModal from "react-modal";
import { axiospost } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { Form, Formik } from "formik";
import { createrefereerule } from "../../utils/validations";
import { FormInput } from "../../components/ui/FormInput";
import { Button } from "../../components/ui/Button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../../components/ui/Card";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlus, FiEdit2, FiUser, FiPhone, FiMail, FiHome, FiSave, FiX, FiUsers } from "react-icons/fi";

/**
 * Modern Referee/Guardian Form Component
 * Features floating action button, modern modal with animations, and improved UX
 *
 * @param {Object} props - Component props
 * @param {Object} props.modules - Existing referee data for editing (optional)
 */
const BtnReferee = ({ modules }) => {
  const [showmodal, setshowmodal] = React.useState(false);
  const queryClient = useQueryClient();

  const _save = async (values) => {
    try {
      const firstname = values?.firstname;
      const middlename = values?.middlename;
      const lastname = values?.lastname;
      const emailaddress = values?.emailaddress;
      const phonenumber = values?.phonenumber;
      const homeaddress = values?.homeaddress;
      const req = await axiospost("ms_applicant_referee", {
        firstname,
        middlename,
        lastname,
        emailaddress,
        phonenumber,
        homeaddress,
      });
      if (req.status !== 200) {
        throw new Error(req?.message);
      }
      const { data } = req;
      if (data && data.type === "success") {
        const { message, type } = data;
        swal(type, message);
        queryClient.invalidateQueries({ queryKey: ["listOfReferees"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
        _close();
      } else {
        throw new Error(data.message);
      }
    } catch ({ message }) {
      swal("warning", message);
    }
  };

  const _close = () => {
    setshowmodal(false);
  };

  const _show = () => {
    setshowmodal(true);
  };

  return (
    <div>
      <AnimatePresence>
        {showmodal && (
          <ReactModal
            isOpen={showmodal}
            onRequestClose={_close}
            className="flex items-center justify-center outline-none"
            overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
            closeTimeoutMS={200}
            ariaHideApp={false}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="w-full min-w-[60%] max-w-5xl max-h-[90vh] overflow-y-auto"
            >
              <Card variant="default" padding="none" className="shadow-2xl">
                {/* Modern Gradient Header */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-6 sm:px-8 text-white rounded-t-xl">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm">
                      <FiUsers className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">
                        {modules ? "Edit Parent/Guardian" : "Add Parent/Guardian"}
                      </h2>
                      <p className="text-primary-100 text-sm mt-1">
                        {modules
                          ? "Update referee information below"
                          : "Provide details of your parent or guardian"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form Content */}
                <CardContent className="p-6 sm:p-8">
                  {/* Info Alert */}
                  <Alert variant="info" className="mb-6">
                    <AlertDescription>
                      All fields marked with an asterisk (*) are required. Please ensure all information is accurate.
                    </AlertDescription>
                  </Alert>

                  <Formik
                    initialValues={{
                      firstname: modules ? modules?.firstname : "",
                      middlename: modules ? modules?.middlename : "",
                      lastname: modules ? modules?.lastname : "",
                      emailaddress: modules ? modules?.emailaddress : "",
                      phonenumber: modules ? modules?.phonenumber : "",
                      homeaddress: modules ? modules?.homeaddress : "",
                    }}
                    validationSchema={createrefereerule}
                    initialTouched={{ zip: true }}
                    onSubmit={_save}
                  >
                    {({ isSubmitting }) => (
                      <Form className="space-y-4">
                        {/* Personal Information Section */}
                        <div>
                          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                            <FiUser className="h-5 w-5 text-primary-600" />
                            Personal Information
                          </h3>

                          {/* Name Fields - Responsive Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <FormInput
                              name="firstname"
                              label="First Name"
                              type="text"
                              leftIcon={<FiUser />}
                              required
                            />
                            <FormInput
                              name="middlename"
                              label="Middle Name"
                              type="text"
                              leftIcon={<FiUser />}
                            />
                            <FormInput
                              name="lastname"
                              label="Last Name"
                              type="text"
                              leftIcon={<FiUser />}
                              required
                            />
                          </div>
                        </div>

                        {/* Contact Information Section */}
                        <div>
                          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                            <FiMail className="h-5 w-5 text-primary-600" />
                            Contact Information
                          </h3>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <FormInput
                              name="emailaddress"
                              label="Email Address"
                              type="email"
                              leftIcon={<FiMail />}
                              helperText="We'll never share your email with anyone"
                            />
                            <FormInput
                              name="phonenumber"
                              label="Phone Number"
                              type="text"
                              leftIcon={<FiPhone />}
                              helperText="Format: +2348xxxxxxxxx"
                              required
                            />
                          </div>

                          <FormInput
                            name="homeaddress"
                            label="Home Address"
                            type="text"
                            leftIcon={<FiHome />}
                            helperText="Complete residential address of the referee"
                            required
                          />
                        </div>

                        {/* Action Buttons */}
                        <CardFooter className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-6 border-t border-neutral-200 px-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="lg"
                            onClick={_close}
                            leftIcon={<FiX className="h-5 w-5" />}
                            className="w-full sm:w-auto"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            loading={isSubmitting}
                            leftIcon={<FiSave className="h-5 w-5" />}
                            className="w-full sm:w-auto"
                          >
                            {isSubmitting ? "Saving..." : modules ? "Update Referee" : "Save Referee"}
                          </Button>
                        </CardFooter>
                      </Form>
                    )}
                  </Formik>
                </CardContent>
              </Card>
            </motion.div>
          </ReactModal>
        )}
      </AnimatePresence>

      {/* Add Button */}
      {!modules && (
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={_show}
          leftIcon={<FiPlus className="h-4 w-4" />}
        >
          Add Guardian
        </Button>
      )}

      {/* Edit Button for Existing Referee */}
      {modules && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={_show}
          className="inline-flex items-center justify-center w-10 h-10 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label="Edit referee information"
        >
          <FiEdit2 className="h-4 w-4" />
        </motion.button>
      )}
    </div>
  );
};

export default BtnReferee;
